/**
 * The native app shell (Capacitor: the Android app now, iOS later), reached through `window.Capacitor` only: the web
 * build has no dependency on @capacitor/* and behaves exactly as before when the global is absent (a browser tab).
 *
 * A native plugin is found in the first way the shell offers:
 *  - `Capacitor.Plugins.<Name>`: the plugin proxies of @capacitor/core, when the app bundles it;
 *  - `Capacitor.registerPlugin(name)`: @capacitor/core present, the plugin not registered yet;
 *  - the bare native bridge (`Capacitor.nativePromise` / `nativeCallback`, listed in `Capacitor.PluginHeaders`): the
 *    WebView of a Capacitor app without @capacitor/core in the page.
 * Everything platform-specific of the client lives in src/client/platform; the game (src/game) and the shared code
 * (src/shared) never import it.
 */

/** The part of the `window.Capacitor` global used here. */
export interface CapacitorLike {
  isNativePlatform?(): boolean;
  getPlatform?(): string;
  Plugins?: Record<string, any>;
  PluginHeaders?: Array<{ name: string }>;
  registerPlugin?(name: string): any;
  nativePromise?(plugin: string, method: string, options?: unknown): Promise<any>;
  nativeCallback?(plugin: string, method: string, options: unknown, callback: (data: any, error?: unknown) => void): unknown;
}

/** A native plugin: promise methods and events. */
export interface NativePlugin {
  call(method: string, options?: Record<string, unknown>): Promise<any>;
  /** Subscribes to an event of the plugin (e.g. App 'backButton'); failures are ignored. */
  listen(event: string, callback: (data: any) => void): void;
}

/** The Capacitor global of a native shell, or null (a browser page, or @capacitor/core running on the web). */
export function capacitor(win: any = globalThis): CapacitorLike | null {
  const cap = win?.Capacitor;
  if (!cap || typeof cap !== 'object') return null;
  try {
    if (typeof cap.isNativePlatform === 'function' && !cap.isNativePlatform()) return null;
  } catch {
    return null;
  }
  return cap as CapacitorLike;
}

/** True inside the native app (Android / iOS WebView of Capacitor). */
export function isNativeShell(win: any = globalThis): boolean {
  return capacitor(win) !== null;
}

/** 'android' / 'ios' inside the native app, else 'web'. */
export function shellPlatform(win: any = globalThis): string {
  const cap = capacitor(win);
  if (!cap) return 'web';
  try {
    return String(cap.getPlatform?.() ?? 'android');
  } catch {
    return 'android';
  }
}

/** The native plugin `name` (e.g. 'Preferences', 'App'), or null when the shell does not have it. */
export function nativePlugin(name: string, win: any = globalThis): NativePlugin | null {
  const cap = capacitor(win);
  if (!cap) return null;
  const hasHeader = Array.isArray(cap.PluginHeaders) && cap.PluginHeaders.some((h) => h && h.name === name);
  let proxy: any = null;
  try {
    proxy = cap.Plugins?.[name] ?? null;
    if (!proxy && hasHeader && typeof cap.registerPlugin === 'function') proxy = cap.registerPlugin(name);
  } catch {
    proxy = null;
  }
  if (proxy) {
    return {
      call: (method, options) => Promise.resolve().then(() => proxy[method](options ?? {})),
      listen: (event, callback) => {
        try {
          void Promise.resolve(proxy.addListener(event, callback)).catch(() => undefined);
        } catch {
          /* no such event on this platform */
        }
      },
    };
  }
  if (hasHeader && typeof cap.nativePromise === 'function') {
    return {
      call: (method, options) => cap.nativePromise!(name, method, options ?? {}),
      listen: (event, callback) => {
        try {
          cap.nativeCallback?.(name, 'addListener', { eventName: event }, (data) => callback(data));
        } catch {
          /* no such event on this platform */
        }
      },
    };
  }
  return null;
}
